<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Navigation -->
    <nav class="bg-white border-b border-gray-200 fixed w-full top-0 z-50">
      <div class="container mx-auto px-4">
        <div class="flex justify-between h-16 items-center">
          <!-- Logo -->
          <NuxtLink :to="localePath('/')" class="flex items-center space-x-2">
            <div class="h-8 w-8 rounded-full bg-gradient-to-br from-primary-600 to-primary-400 flex items-center justify-center text-white font-bold text-lg">
              b
            </div>
            <span class="text-2xl font-bold bg-gradient-to-r from-primary-600 to-primary-400 bg-clip-text text-transparent">
              bLink
            </span>
          </NuxtLink>

          <!-- Navigation Links -->
          <div class="hidden md:flex space-x-1">
            <NuxtLink
              v-for="link in headerLinks"
              :key="link.to"
              :to="link.to"
              class="px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-primary-600 hover:bg-gray-50 transition-colors duration-150"
            >
              {{ link.label }}
            </NuxtLink>
          </div>

          <!-- Right Side -->
          <div class="flex items-center space-x-4">
            <!-- Language Selector -->
            <ClientOnly>
              <UDropdown
                :items="languageItems"
                :popper="{ placement: 'bottom-end' }"
                class="relative"
              >
                <UButton
                  variant="soft"
                  color="emerald"
                  class="flex items-center space-x-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-150 !bg-white !text-gray-700 !border-gray-200"
                >
                  <UIcon
                    :name="currentLocale === 'en' ? 'i-twemoji-flag-united-kingdom' : 'i-twemoji-flag-japan'"
                    class="w-5 h-5"
                  />
                  <span
                  >{{ currentLocale === 'en' ? t('languages.en') : t('languages.ja') }}</span>
                  <UIcon
                    name="i-heroicons-chevron-down-20-solid"
                    class="h-5 w-5 opacity-50"
                  />
                </UButton>
              </UDropdown>
            </ClientOnly>

            <!-- Auth Buttons -->
            <ClientOnly>
              <template v-if="isAuthenticated">
                <UDropdown
                  :items="userMenuItems"
                  :popper="{ placement: 'bottom-end' }"
                  class="relative"
                >
                  <UButton
                    variant="ghost"
                    color="gray"
                    class="flex items-center space-x-2 !text-gray-700 hover:!text-primary-600 hover:!bg-primary-50 transition-colors duration-150"
                  >
                    <UAvatar
                      :src="authStore.user?.avatar"
                      :alt="authStore.user?.username"
                      size="sm"
                      class="bg-primary-50"
                    />
                    <span class="text-sm font-medium">
                      {{ authStore.user?.username }}
                    </span>
                    <UIcon
                      name="i-heroicons-chevron-down-20-solid"
                      class="h-5 w-5 opacity-50"
                    />
                  </UButton>
                </UDropdown>
              </template>
              <template v-else>
                <UButton
                  variant="ghost"
                  :to="localePath('/auth/login')"
                  class="text-gray-700 hover:text-primary-700 hover:bg-primary-50 transition-colors duration-150"
                >
                  {{ t('auth.login.sign_in') }}
                </UButton>
                <UButton
                  color="primary"
                  :to="localePath('/auth/register')"
                  class="bg-primary-500 hover:bg-primary-600 text-white transition-colors duration-150"
                >
                  {{ t('auth.login.sign_up_link') }}
                </UButton>
              </template>
            </ClientOnly>
          </div>
        </div>
      </div>
    </nav>

    <!-- Main Content -->
    <main class="pt-16">
      <div class="container mx-auto px-4 py-8">
        <NuxtPage />
      </div>
    </main>

    <!-- Footer -->
    <footer class="bg-white border-t border-gray-200 mt-auto">
      <div class="container mx-auto px-4 py-8">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
          <!-- Company Info -->
          <div class="space-y-4">
            <h3 class="text-lg font-semibold text-gray-900">{{ t('footer.company') }}</h3>
            <ul class="space-y-2">
              <li>
                <NuxtLink :to="localePath('/about')" class="text-gray-600 hover:text-primary-600">
                  {{ t('footer.about') }}
                </NuxtLink>
              </li>
              <li>
                <NuxtLink :to="localePath('/contact')" class="text-gray-600 hover:text-primary-600">
                  {{ t('footer.contact') }}
                </NuxtLink>
              </li>
            </ul>
          </div>

          <!-- Support -->
          <div class="space-y-4">
            <h3 class="text-lg font-semibold text-gray-900">{{ t('footer.support') }}</h3>
            <ul class="space-y-2">
              <li>
                <NuxtLink :to="localePath('/troubleshooting')" class="text-gray-600 hover:text-primary-600">
                  {{ t('footer.troubleshooting') }}
                </NuxtLink>
              </li>
            </ul>
          </div>

          <!-- Legal -->
          <div class="space-y-4">
            <h3 class="text-lg font-semibold text-gray-900">{{ t('footer.legal') }}</h3>
            <ul class="space-y-2">
              <li>
                <NuxtLink :to="localePath('/privacy')" class="text-gray-600 hover:text-primary-600">
                  {{ t('footer.privacy') }}
                </NuxtLink>
              </li>
              <li>
                <NuxtLink :to="localePath('/terms')" class="text-gray-600 hover:text-primary-600">
                  {{ t('footer.terms') }}
                </NuxtLink>
              </li>
            </ul>
          </div>
        </div>

        <div class="mt-8 pt-8 border-t border-gray-200">
          <p class="text-center text-gray-600">
            © {{ new Date().getFullYear() }} bLink. {{ t('footer.rights') }}
          </p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import type { DropdownItem } from '#ui/types'

const { t, locale } = useI18n()
const localePath = useLocalePath()
const currentLocale = computed(() => locale.value)
const authStore = useAuthStore()
const router = useRouter()

// Check auth status on layout mount
onMounted(async () => {
  if (process.client) {
    await authStore.checkAuth()
  }
})

const headerLinks = computed(() => [
  { label: t('nav.home'), to: localePath('/') },
  { label: t('nav.about'), to: localePath('/about') },
  { label: t('nav.locations'), to: localePath('/locations') },
  { label: t('nav.pricing'), to: localePath('/pricing') },
])

const userMenuItems = computed(() => [[
  {
    label: t(authStore.dashboardLabelKey),
    icon: 'i-heroicons-home',
    to: localePath(authStore.dashboardPath),
    class: 'text-gray-700 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-150'
  },
  {
    label: t('nav.profile'),
    icon: 'i-heroicons-user',
    to: localePath('/profile'),
    class: 'text-gray-700 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-150'
  },
  {
    label: t('auth.logout'),
    icon: 'i-heroicons-arrow-right-on-rectangle',
    click: async () => {
      await authStore.logout()
      router.push(localePath('/'))
    },
    class: 'text-gray-700 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-150'
  }
]])

const languageItems = computed(() => [[
  {
    label: 'English',
    icon: 'i-twemoji-flag-united-kingdom',
    click: () => locale.value = 'en',
  },
  {
    label: '日本語',
    icon: 'i-twemoji-flag-japan',
    click: () => locale.value = 'ja',
  }
]])

const toggleLocale = () => {
  locale.value = locale.value === 'en' ? 'ja' : 'en'
}

// Compute auth status for conditional rendering
const isAuthenticated = computed(() => authStore.isAuthenticated)
</script>

<style lang="postcss">
.router-link-active {
  @apply text-primary-600 bg-primary-50;
}

/* Gradient animation for the logo text */
.text-gradient {
  background-size: 200% auto;
  animation: shine 2s linear infinite;
}

@keyframes shine {
  to {
    background-position: 200% center;
  }
}

/* Force light styling for dropdowns */
:deep(.ui-dropdown) {
  background-color: white !important;
  border-color: #e5e7eb !important;
}

:deep(.ui-dropdown-item) {
  background-color: white !important;
  color: #374151 !important;
}

:deep(.ui-dropdown-item:hover) {
  background-color: #f3f4f6 !important;
  color: #111827 !important;
}

:deep(.ui-dropdown-item.active) {
  background-color: #ecfdf5 !important;
  color: #059669 !important;
}

:deep(.ui-dropdown-container) {
  background-color: white !important;
  border-color: #e5e7eb !important;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
}

/* Force light styling for dropdown buttons */
:deep(.ui-button) {
  background-color: white !important;
  color: #374151 !important;
  border-color: #e5e7eb !important;
}

:deep(.ui-button:hover) {
  background-color: #f3f4f6 !important;
  color: #111827 !important;
}

:deep(.ui-button:focus) {
  background-color: white !important;
  color: #374151 !important;
  border-color: #10b981 !important;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1) !important;
}

/* Specific overrides for emerald variant */
:deep(.ui-button[data-variant="soft"][data-color="emerald"]) {
  background-color: white !important;
  color: #374151 !important;
  border-color: #e5e7eb !important;
}

:deep(.ui-button[data-variant="soft"][data-color="emerald"]:hover) {
  background-color: #f3f4f6 !important;
  color: #111827 !important;
}
</style> 